/**
 * VAVCI · «Completa la rutina» (PDP, WU3 rediseño Concept).
 *
 * La sección vavci-product-bundle pinta el producto actual (fila fija, siempre
 * marcada) + hasta 2-3 complementarios. La fuente de los complementarios:
 *   · Ajustes product_2/product_3 de la sección (elección manual) → mandan y se
 *     renderizan en servidor; no se pide nada a la red.
 *   · Sin elección manual → el motor nativo de Shopify vía
 *     routes.product_recommendations_url con section_id + intent=complementary
 *     (mismo truco que vavci-related.js y las recomendaciones del carrito): la
 *     PROPIA sección se re-renderiza en el endpoint y de ahí se extraen las
 *     filas ya pintadas (precios reales resueltos en Liquid). Sin resultados,
 *     la sección entera queda oculta.
 *
 * «Añadir el pack completo» añade todas las filas marcadas en UN solo POST a
 * /cart/add.js (items[]) siguiendo la misma secuencia post-alta que
 * vavci-pdp-cart.js: CartLinesUpdateEvent optimista → POST → /cart.js →
 * window.vcShipBar → abrir el drawer de Horizon. (Aquel archivo no se toca:
 * este módulo replica sus convenciones.)
 *
 * La lógica de totales es PURA y se testea en Node (scripts/__tests__/
 * pdp-bundle.test.js vía scripts/lib/pdp-bundle.js); el DOM queda guardado al
 * final, igual que en vavci-pdp-variants.js.
 */
/**
 * Formato de moneda SIN import relativo. vavci-pdp-variants.js (que la sección
 * principal carga con URL versionada `?v=`) publica window.vcMoney en su
 * top-level y se ejecuta antes en el orden del documento. Un
 * `import ... from './vavci-pdp-variants.js'` pediría la URL SIN `?v=` y el CDN
 * de Shopify la sirve de una caché VIEJA (roto en producción el 2026-08-27:
 * «does not provide an export named 'formatEuros'» mataba este módulo entero).
 * La resolución es EN CADA LLAMADA, nunca capturada en el top-level; si el
 * global faltara, un formato mínimo evita el crash.
 * @param {number} cents
 * @returns {string}
 */
export function formatEuros(cents) {
  if (typeof window !== 'undefined' && typeof window.vcMoney === 'function') return window.vcMoney(cents);
  const n = Number(cents);
  return ((Number.isFinite(n) ? n : 0) / 100).toFixed(2).replace('.', ',') + ' €';
}

/**
 * Resumen del pack a partir del estado de las filas. Solo cuentan las marcadas.
 * El «antes» (compareCents) suma compare_at cuando es una rebaja REAL
 * (compare > precio) y el precio normal cuando no la hay — así el tachado nunca
 * queda por debajo del total. saveCents sale solo de deltas reales (0 = ocultar).
 * @param {Array<{checked: boolean, cents: number, ccents: number}>} rows
 * @returns {{totalCents: number, compareCents: number, saveCents: number, count: number}}
 */
export function computeBundleSummary(rows) {
  let totalCents = 0;
  let compareCents = 0;
  let count = 0;
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || !row.checked) continue;
    count += 1;
    const cents = Number(row.cents);
    const price = Number.isFinite(cents) && cents > 0 ? cents : 0;
    const ccents = Number(row.ccents);
    // El compare_at solo cuenta si el precio de la fila es real: un precio sucio
    // (0/negativo/no numérico) no puede fabricar un ahorro de la nada.
    const compare = price > 0 && Number.isFinite(ccents) && ccents > price ? ccents : price;
    totalCents += price;
    compareCents += compare;
  }
  return { totalCents, compareCents, saveCents: compareCents - totalCents, count };
}

/**
 * Líneas del POST /cart/add.js: una por fila marcada con variante válida,
 * cantidad 1, sin duplicados (el motor podría repetir un producto ya elegido).
 * @param {Array<{checked: boolean, variantId?: string}>} rows
 * @returns {Array<{id: number, quantity: 1}>}
 */
export function collectPackItems(rows) {
  const items = [];
  const seen = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || !row.checked) continue;
    const id = String(row.variantId ?? '');
    if (!/^\d+$/.test(id) || seen.has(id)) continue;
    seen.add(id);
    items.push({ id: Number(id), quantity: 1 });
  }
  return items;
}

/* ── Enganche con el DOM ──────────────────────────────────────────────────── */

// El importmap de Horizon resuelve '@shopify/events'; en Node (tests) ni se
// intenta. Si fallara en el navegador, el alta sigue funcionando sin el evento
// optimista (el drawer se abre igual y se refresca al re-render de sections).
const eventsModule = typeof document !== 'undefined' ? import('@shopify/events').catch(() => null) : null;

const ROOT = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function readRows(section) {
  return Array.from(section.querySelectorAll('[data-vc-bundle-row]')).map((el) => {
    const check = el.querySelector('[data-vc-bundle-check]');
    return {
      checked: Boolean(check && check.checked),
      cents: Number(el.getAttribute('data-cents')),
      ccents: Number(el.getAttribute('data-ccents')),
      variantId: el.getAttribute('data-variant-id') || '',
    };
  });
}

function repaint(section) {
  const s = computeBundleSummary(readRows(section));
  const total = section.querySelector('[data-vc-bundle-total]');
  const was = section.querySelector('[data-vc-bundle-was]');
  const save = section.querySelector('[data-vc-bundle-save]');
  const count = section.querySelector('[data-vc-bundle-count]');
  const add = section.querySelector('[data-vc-bundle-add]');
  if (total) total.textContent = formatEuros(s.totalCents);
  if (was) {
    was.textContent = formatEuros(s.compareCents);
    was.hidden = s.saveCents <= 0;
  }
  if (save) {
    // Plantilla i18n servida por Liquid con placeholder ### (una { literal rompe el tag {{ }} de Shopify).
    save.textContent = (save.getAttribute('data-t-save') || '###').replace('###', formatEuros(s.saveCents));
    save.hidden = s.saveCents <= 0;
  }
  if (count) {
    count.textContent = (count.getAttribute('data-t-count') || '###').replace('###', String(s.count));
  }
  if (add) add.disabled = s.count === 0;
}

/** Abre el cart-drawer de Horizon (mismos ganchos que vavci-pdp-cart.js). */
function openCartDrawer() {
  const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
  if (trigger) { trigger.click(); return true; }
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
  return false;
}

async function addPack(section, button) {
  if (button.getAttribute('aria-busy') === 'true') return;
  const items = collectPackItems(readRows(section));
  if (!items.length) return;
  button.setAttribute('aria-busy', 'true');

  const mod = eventsModule ? await eventsModule : null;
  const CartLinesUpdateEvent = mod && mod.CartLinesUpdateEvent;

  // Re-render de las secciones del carrito para refrescar el drawer sin recargar.
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });

  const body = { items };
  if (sectionIds.size) body.sections = [...sectionIds].join(',');

  let deferred = null;
  if (CartLinesUpdateEvent) {
    deferred = CartLinesUpdateEvent.createPromise();
    // Evento optimista: cart-icon.js sube el badge al instante.
    section.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (data.status) { // error (p. ej. sin stock)
      if (deferred) deferred.resolve({ detail: { didError: true, source: 'vavci-pdp-bundle' } });
      console.warn('[vavci-pdp-bundle] add pack error:', data.description || data.message || data.status);
      return;
    }

    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    // Barra de envío gratis de la PDP: mismo total, sin peticiones extra.
    if (typeof window.vcShipBar === 'function') window.vcShipBar(cart.total_price);

    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'vavci-pdp-bundle', itemCount: items.length, sections: data.sections, didError: false },
      });
    }

    if (!openCartDrawer()) window.location.href = `${ROOT}cart`;
  } catch (error) {
    if (deferred && typeof deferred.reject === 'function') deferred.reject(error);
    console.warn('[vavci-pdp-bundle] add pack failed:', error);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/**
 * Sin elección manual: pide al endpoint de recomendaciones que renderice ESTA
 * misma sección (intent=complementary) y extrae las filas ya pintadas. Silencioso
 * ante cualquier fallo: la sección se queda oculta (nunca un hueco ni un error).
 */
async function fetchComplementary(section, rowsBox) {
  try {
    const url = new URL(section.getAttribute('data-url'), window.location.origin);
    if (url.origin !== window.location.origin) return; // jamás seguir un data-url de otro origen
    const res = await fetch(url.toString(), { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.querySelectorAll('[data-vc-bundle-row]');
    if (!fresh.length) return;

    const seed = section.getAttribute('data-product-id') || '';
    const max = Math.max(1, parseInt(section.getAttribute('data-limit') || '3', 10) || 3);
    const present = new Set(Array.from(rowsBox.querySelectorAll('[data-vc-bundle-row]')).map((r) => r.getAttribute('data-product-id')));
    let added = 0;
    for (const row of fresh) {
      if (added >= max) break;
      const pid = row.getAttribute('data-product-id') || '';
      if (!pid || pid === seed || present.has(pid)) continue;
      present.add(pid);
      rowsBox.appendChild(row);
      added += 1;
    }
    if (!added) return;
    section.hidden = false;
    repaint(section);
  } catch (e) { /* red caída → la sección sigue oculta */ }
}

function init(section) {
  const rowsBox = section.querySelector('[data-vc-bundle-rows]');
  if (!rowsBox) return;

  rowsBox.addEventListener('change', (ev) => {
    if (ev.target.closest('[data-vc-bundle-check]')) repaint(section);
  });

  const addBtn = section.querySelector('[data-vc-bundle-add]');
  if (addBtn) addBtn.addEventListener('click', () => addPack(section, addBtn));

  repaint(section);

  // Solo hay data-url cuando NO hay elección manual; la sección sale del
  // servidor con hidden y display:none, así que un IntersectionObserver nunca
  // dispararía (una caja oculta no interseca). Se pide en el primer respiro.
  if (section.hasAttribute('data-url')) {
    const load = () => fetchComplementary(section, rowsBox);
    if ('requestIdleCallback' in window) requestIdleCallback(load, { timeout: 2500 });
    else setTimeout(load, 400);
  }
}

if (typeof document !== 'undefined') {
  const boot = () => document.querySelectorAll('[data-vc-bundle]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
